import { Dispatch, memo, SetStateAction, useMemo, useState } from "react";
import Accordion from "../../../components/accordian";
import { useTranslation } from "react-i18next";
import {
  Controller,
  UseFormClearErrors,
  UseFormSetValue,
} from "react-hook-form";
import ComboboxInput from "../../../components/inputs/ComboboxInput";
import DateTimePickerInput from "../../../components/inputs/DateTimePickerInput";
import { formattedDateTime, mapToOptions } from "../../../lib/functions";
import { APP_ROUTES } from "../../../routes";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";
import TextInput from "../../../components/inputs/TextInput";
import { useGetAllSystemDetailCodesDropdown } from "../../../hooks/common/query";
import {
  PERMIT_TO_WORK_CATEGORIES,
  SYSTEM_DETAIL_CODES,
} from "../../../constants/common";
import AreaAccessCard from "../parking-sticker-request/AreaAccessCard";

interface PermitDetailsProps {
  control: any;
  errors: any;
  isFieldsDisabled?: boolean;
  clearErrors: UseFormClearErrors<{
    permitStartDate: string;
    permitEndDate: string;
    affectedService: string;
  }>;
  setValue: UseFormSetValue<{
    affectedService: string;
    permitStartDate: string;
    permitEndDate: string;
    workCategory: string;
    highRiskWorkCategory?: string;
    scopeOfWork: string;
    workType: string;
    floorNo: string;
    officeNumber: string;
  }>;
  permitStartDate: Date;
  selectedWorkCategory: string;
  setSelectedWorkCategory: Dispatch<SetStateAction<string>>;
  checkedBuildingIds: number[];
  setCheckedBuildingIds: Dispatch<SetStateAction<number[]>>;
}

const PermitDetails = ({
  control,
  errors,
  isFieldsDisabled,
  clearErrors,
  setValue,
  permitStartDate,
  selectedWorkCategory,
  setSelectedWorkCategory,
  checkedBuildingIds,
  setCheckedBuildingIds,
}: PermitDetailsProps) => {
  const { t } = useTranslation();

  const location = useLocation();

  const [selectedKey, setSelectedKey] = useState<string>("1");

  const { data: allAffectedServices } = useGetAllSystemDetailCodesDropdown({
    encryptedId: SYSTEM_DETAIL_CODES.PERMIT_AFFECTED_SERVICES,
  });

  const { data: allWorkCategories } = useGetAllSystemDetailCodesDropdown({
    encryptedId: SYSTEM_DETAIL_CODES.PERMIT_WORK_CATEGORY,
  });

  const { data: allHighRiskWorkCategories } =
    useGetAllSystemDetailCodesDropdown({
      encryptedId: SYSTEM_DETAIL_CODES.PERMIT_HIGH_RISK_WORK_CATEGORY,
    });

  const { data: allWorkTypes } = useGetAllSystemDetailCodesDropdown({
    encryptedId: SYSTEM_DETAIL_CODES.PERMIT_WORK_TYPE,
  });

  const affectedServices = useMemo(
    () =>
      mapToOptions(
        "s_Desc_E",
        "s_Desc_A",
        "n_DetailedCode",
        allAffectedServices,
      ),
    [allAffectedServices],
  );

  const workCategories = useMemo(
    () =>
      mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", allWorkCategories),
    [allWorkCategories],
  );

  const highRiskWorkCategories = useMemo(
    () =>
      mapToOptions(
        "s_Desc_E",
        "s_Desc_A",
        "n_DetailedCode",
        allHighRiskWorkCategories,
      ),
    [allHighRiskWorkCategories],
  );

  const workTypes = useMemo(
    () => mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", allWorkTypes),
    [allWorkTypes],
  );

  return (
    <div className="bg-white z-10 p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordion
        id="1"
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 -mt-2 items-center">
            {t("1")}. {t("permitDetails")}
          </h1>
        }
      >
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
          <Controller
            name="affectedService"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <ComboboxInput
                options={affectedServices}
                value={
                  affectedServices?.find((item) => item.value === Number(value))
                    ?.labelEn || ""
                }
                onChange={(value) => {
                  onChange(value);
                }}
                error={errors?.[name]?.message}
                label={t("affectedService")}
                required
                readOnly={isFieldsDisabled}
              />
            )}
          />

          <Controller
            name="permitStartDate"
            control={control}
            render={({ field: { name, value, onChange } }) => (
              <DateTimePickerInput
                label={t("permitStartDate")}
                value={value}
                required
                readOnly={isFieldsDisabled}
                onChange={(value) => {
                  if (!value) return;

                  onChange(value);
                  clearErrors(name);

                  // Set permitEndDate to permitStartDate + 1 hour
                  const newEndDate = new Date(value);
                  newEndDate.setHours(newEndDate.getHours() + 1);
                  setValue("permitEndDate", formattedDateTime(newEndDate));
                }}
                error={errors?.[name]?.message}
                minDate={
                  location.pathname !== APP_ROUTES.UPDATE_PERMIT_TO_WORK
                    ? new Date()
                    : undefined
                }
                dateTimeValidation={(newDate: Date) => {
                  if (newDate < new Date()) {
                    toast.error(t("permitStartTimeCannotBeInPast"));
                    return false;
                  }
                  return true;
                }}
              />
            )}
          />

          <Controller
            name="permitEndDate"
            control={control}
            render={({ field: { name, value, onChange } }) => {
              return (
                <DateTimePickerInput
                  label={t("permitEndDate")}
                  value={value}
                  required
                  readOnly={isFieldsDisabled}
                  onChange={(value) => {
                    onChange(value);
                    clearErrors(name);
                  }}
                  error={errors?.[name]?.message}
                  minDate={new Date()}
                  dateTimeValidation={(newDate: Date) => {
                    if (newDate < permitStartDate) {
                      toast.error(t("permitEndTimeGreaterThanStartTime"));
                      return false;
                    }
                    return true;
                  }}
                />
              );
            }}
          />

          <Controller
            name="workCategory"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <ComboboxInput
                options={workCategories}
                value={
                  workCategories?.find((item) => item.value === Number(value))
                    ?.labelEn || ""
                }
                onChange={(value) => {
                  onChange(value);
                  setSelectedWorkCategory(value.toString());
                }}
                error={errors?.[name]?.message}
                label={t("workCategory")}
                required
                readOnly={isFieldsDisabled}
              />
            )}
          />

          {selectedWorkCategory ===
            PERMIT_TO_WORK_CATEGORIES.HIGH_RISK_WORKS && (
              <Controller
                name="highRiskWorkCategory"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={highRiskWorkCategories}
                    value={
                      highRiskWorkCategories?.find(
                        (item) => item.value === Number(value),
                      )?.labelEn || ""
                    }
                    onChange={(value) => {
                      onChange(value);
                    }}
                    error={errors?.[name]?.message}
                    label={t("highRiskWorkCategory")}
                    required
                    readOnly={isFieldsDisabled}
                  />
                )}
              />
            )}

          <Controller
            name="workType"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <ComboboxInput
                options={workTypes}
                value={
                  workTypes?.find((item) => item.value === Number(value))
                    ?.labelEn || ""
                }
                onChange={(value) => {
                  onChange(value);
                }}
                error={errors?.[name]?.message}
                label={t("workType")}
                required
                readOnly={isFieldsDisabled}
              />
            )}
          />

          <Controller
            name="floorNo"
            control={control}
            render={({ field: { onChange, name, value } }) => {
              return (
                <TextInput
                  label={t("floorNo")}
                  value={value || ""}
                  onChange={onChange}
                  error={errors?.[name]?.message}
                  required
                  readOnly={isFieldsDisabled}
                />
              );
            }}
          />

          <Controller
            name="officeNumber"
            control={control}
            render={({ field: { onChange, name, value } }) => {
              return (
                <TextInput
                  label={t("officeNo")}
                  value={value || ""}
                  onChange={onChange}
                  error={errors?.[name]?.message}
                  required
                  readOnly={isFieldsDisabled}
                />
              );
            }}
          />
        </div>

        <div className="bg-gray-200 h-[1px] my-4" />

        <AreaAccessCard
          checkedBuildingIds={checkedBuildingIds}
          setCheckedBuildingIds={setCheckedBuildingIds}
          isFieldsDisabled={isFieldsDisabled}
        />
      </Accordion>
    </div>
  );
};

export default memo(PermitDetails);
